import { Box, Button, Checkbox, Divider, Flex, Form, Popup, Text, toast } from '@approvalmax/ui/src/components';
import { selectors } from 'modules/common';
import { backend } from 'modules/data';
import { memo } from 'react';
import { useForm } from 'react-hook-form';
import { UserAvatar } from 'shared/components';
import { useUpdateParticipantPermissions } from 'shared/data/webApp/v1';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { usePermissionsSettings } from './PermissionsPopupContent.hooks';
import { messages } from './PermissionsPopupContent.messages';
import { PermissionsPopupContentProps } from './PermissionsPopupContent.types';

export const PermissionsPopupContent = memo<PermissionsPopupContentProps>((props) => {
    const { user, onPermissionsChanged } = props;

    const company = useCompanyBasedOnUrl();

    const { mutate: updateParticipantPermissions, isLoading: isLoadingUpdateParticipantPermissions } =
        useUpdateParticipantPermissions();

    const { viewPaymentDetails, addPaymentDetails, editPaymentDetails } = usePermissionsSettings(
        company,
        user.databaseId ?? ''
    );

    const isAccountOwner = company.author === user.id;

    const form = useForm({
        values: {
            canViewPaymentDetails: viewPaymentDetails?.isEnabled ?? false,
            canAddPaymentDetails: addPaymentDetails?.isEnabled ?? false,
            canEditPaymentDetails: editPaymentDetails?.isEnabled ?? false,
        },
    });

    const onSubmit = form.handleSubmit(async (data) => {
        const newPermissions: backend.Permission[] = [];

        if (data.canViewPaymentDetails) {
            newPermissions.push(backend.Permission.PaymentDetailsView);
        }

        if (data.canAddPaymentDetails) {
            newPermissions.push(backend.Permission.PaymentDetailsAdd);
        }

        if (data.canEditPaymentDetails) {
            newPermissions.push(backend.Permission.PaymentDetailsEdit);
        }

        updateParticipantPermissions(
            {
                params: {
                    path: {
                        companyId: company.id,
                        participantId: user.databaseId ?? '',
                    },
                },
                body: {
                    permissions: newPermissions,
                },
            },
            {
                onSuccess: () => {
                    toast.success(messages.successMessage);

                    onPermissionsChanged();
                },
            }
        );
    });

    return (
        <Form form={form} onSubmit={onSubmit}>
            <Popup.Header
                title={messages.title}
                actions={
                    <Button color='blue80' size='medium' type='submit'>
                        {messages.save}
                    </Button>
                }
                progress={isLoadingUpdateParticipantPermissions}
            />

            <Popup.Body>
                <Flex direction='column' spacing='16'>
                    <Box color='midnight20' radius='small' spacing='24'>
                        <Flex alignItems='center'>
                            <UserAvatar user={user} size={36} />

                            <Flex direction='column' spacing='8'>
                                <Flex spacing='8'>
                                    <Text color='midnight100' font='label' fontSize='medium' fontWeight='medium'>
                                        {user.displayName}
                                    </Text>

                                    <Text color='midnight70' font='label' fontSize='medium' fontWeight='regular'>
                                        {isAccountOwner
                                            ? messages.accountOwner
                                            : selectors.company.getCompanyUserRoleText(user.role)}
                                    </Text>
                                </Flex>

                                <Text color='midnight70' font='label' fontSize='small' fontWeight='regular'>
                                    {user.userEmail}
                                </Text>
                            </Flex>
                        </Flex>
                    </Box>

                    <div>
                        <Flex>
                            <Box width={200} spacing='16 0'>
                                <Text color='midnight70' font='label' fontSize='small' fontWeight='regular'>
                                    {messages.contactPaymentDetailsGroup}
                                </Text>
                            </Box>

                            <Box spacing='0 8'>
                                <Flex alignItems='center' grow={1} height='100%'>
                                    <Checkbox
                                        name='canViewPaymentDetails'
                                        size='small'
                                        disabled={!viewPaymentDetails?.isEditable}
                                    >
                                        {messages.canView}
                                    </Checkbox>

                                    <Checkbox
                                        name='canAddPaymentDetails'
                                        size='small'
                                        disabled={!addPaymentDetails?.isEditable}
                                    >
                                        {messages.canAdd}
                                    </Checkbox>

                                    <Checkbox
                                        name='canEditPaymentDetails'
                                        size='small'
                                        disabled={!editPaymentDetails?.isEditable}
                                    >
                                        {messages.canEdit}
                                    </Checkbox>
                                </Flex>
                            </Box>
                        </Flex>

                        <Divider />
                    </div>
                </Flex>
            </Popup.Body>
        </Form>
    );
});

PermissionsPopupContent.displayName = 'PermissionsPopupContent';
